/**
 * Electron configurations of atoms and ions.
 *
 * Neutral atoms come from data (`electron-config-data.ts`), not from a rule, because the
 * rule has exceptions. The Madelung (Aufbau) order — fill by increasing n + l, ties by
 * lower n — is generated here only to find those exceptions by comparison and to decide
 * where an anion's extra electrons go.
 *
 * ## Ions
 *
 * - **Cations** lose electrons from the outermost occupied subshells first: highest n, then
 *   highest l, and valence subshells before the noble-gas core. That removes 4s before 3d in
 *   a transition metal, and 6s, then 5d, then 4f in a lanthanide, which is how those ions are
 *   written in general chemistry. Real ions occasionally differ; check NIST ASD for
 *   spectroscopic ground states.
 * - **Anions** add electrons to the next subshell in Madelung order that has room.
 */

import {
  FIRST_PREDICTED_Z,
  GROUND_STATES,
  NOBLE_GASES,
  SUBSHELL_LETTERS,
} from "@/lib/tool-kit/chem-reactions/electron-config-data";
import { ELEMENTS } from "@/lib/tool-kit/chemistry/elements";
import { superscriptInt } from "@/lib/tool-kit/chemistry/format";

export interface Subshell {
  n: number;
  l: number;
  electrons: number;
}

export const MAX_N = 8;

export function capacity(l: number): number {
  return 2 * (2 * l + 1);
}

export function subshellLabel(shell: { n: number; l: number }): string {
  return `${shell.n}${SUBSHELL_LETTERS[shell.l]}`;
}

/* ------------------------------------------------------------- parsing */

const TOKEN = /(\d)([spdf])(\d+)/g;
const CORE = /^\[([A-Z][a-z])\]\s*/;

function parseTokens(text: string): Subshell[] {
  const out: Subshell[] = [];
  for (const match of text.matchAll(TOKEN)) {
    out.push({
      n: Number(match[1]),
      l: SUBSHELL_LETTERS.indexOf(
        match[2] as (typeof SUBSHELL_LETTERS)[number]
      ),
      electrons: Number(match[3]),
    });
  }
  return out;
}

function mergeShells(shells: readonly Subshell[]): Subshell[] {
  const map = new Map<string, Subshell>();
  for (const shell of shells) {
    const key = `${shell.n}.${shell.l}`;
    const known = map.get(key);
    if (known) {
      known.electrons += shell.electrons;
    } else {
      map.set(key, { ...shell });
    }
  }
  return [...map.values()];
}

const cache = new Map<string, Subshell[]>();

function expand(entry: string): Subshell[] {
  const hit = cache.get(entry);
  if (hit) {
    return hit.map((s) => ({ ...s }));
  }
  const core = CORE.exec(entry);
  const rest = core ? entry.slice(core[0].length) : entry;
  const inner = core ? expand(GROUND_STATES[symbolZ(core[1]) - 1]) : [];
  const result = mergeShells([...inner, ...parseTokens(rest)]);
  cache.set(entry, result);
  return result.map((s) => ({ ...s }));
}

function symbolZ(symbol: string): number {
  const noble = NOBLE_GASES.find((gas) => gas.symbol === symbol);
  if (!noble) {
    throw new Error(`unknown core ${symbol}`);
  }
  return noble.z;
}

/** The ground-state subshell occupancies of the neutral atom with atomic number `z`. */
export function neutralConfig(z: number): Subshell[] {
  return expand(GROUND_STATES[z - 1]);
}

/** The noble-gas core written in the data entry for `z`, or null (H, He). */
export function dataCore(z: number): string | null {
  return CORE.exec(GROUND_STATES[z - 1])?.[1] ?? null;
}

export function isPredicted(z: number): boolean {
  return z >= FIRST_PREDICTED_Z;
}

/* ----------------------------------------------------- Madelung order */

/** Subshells in the order the Madelung rule fills them, up to 8s. */
export const MADELUNG_ORDER: readonly { n: number; l: number }[] = (() => {
  const all: { n: number; l: number }[] = [];
  for (let n = 1; n <= MAX_N; n++) {
    for (let l = 0; l <= Math.min(n - 1, 3); l++) {
      all.push({ n, l });
    }
  }
  return all.sort((a, b) => a.n + a.l - (b.n + b.l) || a.n - b.n);
})();

function madelungIndex(shell: { n: number; l: number }): number {
  return MADELUNG_ORDER.findIndex((s) => s.n === shell.n && s.l === shell.l);
}

/** The configuration the Madelung rule predicts for `z` electrons. */
export function aufbauConfig(electrons: number): Subshell[] {
  const out: Subshell[] = [];
  let left = electrons;
  for (const shell of MADELUNG_ORDER) {
    if (left <= 0) {
      break;
    }
    const put = Math.min(left, capacity(shell.l));
    out.push({ ...shell, electrons: put });
    left -= put;
  }
  return out;
}

function sameConfig(a: readonly Subshell[], b: readonly Subshell[]): boolean {
  const key = (s: Subshell) => `${s.n}.${s.l}.${s.electrons}`;
  const setA = new Set(a.filter((s) => s.electrons > 0).map(key));
  const setB = new Set(b.filter((s) => s.electrons > 0).map(key));
  return setA.size === setB.size && [...setA].every((k) => setB.has(k));
}

export interface ExceptionEntry {
  z: number;
  symbol: string;
  actual: Subshell[];
  predicted: Subshell[];
}

/** Measured elements (Z ≤ 108) whose configuration differs from the Madelung prediction. */
export function configExceptions(): ExceptionEntry[] {
  const out: ExceptionEntry[] = [];
  for (const element of ELEMENTS) {
    if (element.isotope || element.z >= FIRST_PREDICTED_Z) {
      continue;
    }
    const actual = neutralConfig(element.z);
    const predicted = aufbauConfig(element.z);
    if (!sameConfig(actual, predicted)) {
      out.push({ z: element.z, symbol: element.symbol, actual, predicted });
    }
  }
  return out;
}

/* ----------------------------------------------------------------- ions */

export type IonResult =
  | { ok: true; config: Subshell[] }
  | { ok: false; reason: "tooManyRemoved" | "tooManyAdded" };

const MAX_ANION = 4;

function removalOrder(z: number, config: readonly Subshell[]): Subshell[] {
  const core = dataCore(z);
  const coreKeys = new Set(
    core
      ? expand(GROUND_STATES[symbolZ(core) - 1]).map((s) => `${s.n}.${s.l}`)
      : []
  );
  const rank = (s: Subshell) => (coreKeys.has(`${s.n}.${s.l}`) ? 0 : 1);
  return [...config].sort(
    (a, b) => rank(b) - rank(a) || b.n - a.n || b.l - a.l
  );
}

function removeElectrons(z: number, count: number): IonResult {
  const config = neutralConfig(z);
  if (count > z) {
    return { ok: false, reason: "tooManyRemoved" };
  }
  let left = count;
  for (const shell of removalOrder(z, config)) {
    if (left === 0) {
      break;
    }
    const take = Math.min(left, shell.electrons);
    shell.electrons -= take;
    left -= take;
  }
  return { ok: true, config: config.filter((s) => s.electrons > 0) };
}

function addElectrons(z: number, count: number): IonResult {
  if (count > MAX_ANION) {
    return { ok: false, reason: "tooManyAdded" };
  }
  const config = neutralConfig(z);
  let left = count;
  for (const slot of MADELUNG_ORDER) {
    if (left === 0) {
      break;
    }
    const existing = config.find((s) => s.n === slot.n && s.l === slot.l);
    const have = existing?.electrons ?? 0;
    const room = capacity(slot.l) - have;
    const put = Math.min(room, left);
    if (put <= 0) {
      continue;
    }
    if (existing) {
      existing.electrons += put;
    } else {
      config.push({ ...slot, electrons: put });
    }
    left -= put;
  }
  return { ok: true, config };
}

/** The configuration of element `z` with net charge `charge`. */
export function ionConfig(z: number, charge: number): IonResult {
  if (charge === 0) {
    return { ok: true, config: neutralConfig(z) };
  }
  return charge > 0 ? removeElectrons(z, charge) : addElectrons(z, -charge);
}

/* ---------------------------------------------------------- formatting */

export type Notation = "full" | "noble";
export type ShellOrder = "shell" | "filling";

export interface ConfigSegment {
  text: string;
  /** The electron count as a superscript run; "" for a plain segment. */
  sup: string;
}

export function sortShells(
  shells: readonly Subshell[],
  order: ShellOrder
): Subshell[] {
  return [...shells].sort((a, b) =>
    order === "filling"
      ? madelungIndex(a) - madelungIndex(b)
      : a.n - b.n || a.l - b.l
  );
}

/** The largest noble gas whose every subshell is completely filled in `config`. */
export function nobleCore(config: readonly Subshell[]): {
  symbol: string;
  shells: Subshell[];
} | null {
  for (const gas of [...NOBLE_GASES].reverse()) {
    const shells = expand(GROUND_STATES[gas.z - 1]);
    const covered = shells.every((core) =>
      config.some(
        (s) =>
          s.n === core.n && s.l === core.l && s.electrons === capacity(core.l)
      )
    );
    if (covered) {
      return { symbol: gas.symbol, shells };
    }
  }
  return null;
}

/** Segments for `1s² 2s² …` or `[Ar] 3d⁵ 4s¹`, ready for sub/superscript rendering. */
export function configSegments(
  config: readonly Subshell[],
  notation: Notation,
  order: ShellOrder
): ConfigSegment[] {
  let shells = [...config];
  const segments: ConfigSegment[] = [];
  if (notation === "noble") {
    const core = nobleCore(config);
    if (core) {
      segments.push({ text: `[${core.symbol}]`, sup: "" });
      shells = shells.filter(
        (s) => !core.shells.some((c) => c.n === s.n && c.l === s.l)
      );
    }
  }
  for (const shell of sortShells(shells, order)) {
    segments.push({ text: subshellLabel(shell), sup: String(shell.electrons) });
  }
  return segments;
}

/** The same configuration as plain Unicode text: `[Ar] 3d⁵ 4s¹`. */
export function configText(
  config: readonly Subshell[],
  notation: Notation,
  order: ShellOrder
): string {
  return configSegments(config, notation, order)
    .map((seg) =>
      seg.sup ? `${seg.text}${superscriptInt(Number(seg.sup))}` : seg.text
    )
    .join(" ");
}

/* ----------------------------------------------------------- orbitals */

export interface OrbitalBox {
  /** Arrows in the box: 0, 1 (up) or 2 (up and down). */
  electrons: 0 | 1 | 2;
}

/** Hund's rule: one electron in each orbital of the subshell before any pairs up. */
export function orbitalBoxes(shell: Subshell): OrbitalBox[] {
  const boxes = 2 * shell.l + 1;
  return Array.from({ length: boxes }, (_unused, index) => {
    let electrons = 0;
    if (index < shell.electrons) {
      electrons += 1;
    }
    if (shell.electrons > boxes && index < shell.electrons - boxes) {
      electrons += 1;
    }
    return { electrons: electrons as 0 | 1 | 2 };
  });
}

export function unpairedElectrons(config: readonly Subshell[]): number {
  return config.reduce(
    (sum, shell) =>
      sum + orbitalBoxes(shell).filter((box) => box.electrons === 1).length,
    0
  );
}

export function totalElectrons(config: readonly Subshell[]): number {
  return config.reduce((sum, shell) => sum + shell.electrons, 0);
}

/** Electrons in the highest occupied shell, counting every subshell with that n. */
export function outerShell(config: readonly Subshell[]): {
  n: number;
  electrons: number;
} {
  const n = Math.max(...config.map((s) => s.n));
  return {
    n,
    electrons: config
      .filter((s) => s.n === n)
      .reduce((sum, s) => sum + s.electrons, 0),
  };
}

/* ----------------------------------------------------- reading the input */

export type SpeciesInput =
  | { ok: true; z: number; symbol: string; name: string; charge: number }
  | { ok: false; reason: "empty" | "unknown" | "badCharge" };

const ION_SUFFIX = /^(.*?)\s*\^?\s*\(?\s*(\d*)\s*([+\-−]+)\s*\)?$/;
const INTEGER = /^\d{1,3}$/;
const LEADING_PLUS = /^\+/;

function findElement(token: string) {
  if (INTEGER.test(token)) {
    return ELEMENTS.find((e) => e.z === Number(token) && !e.isotope);
  }
  const lower = token.toLowerCase();
  return ELEMENTS.find(
    (e) =>
      !e.isotope &&
      (e.symbol.toLowerCase() === lower || e.name.toLowerCase() === lower)
  );
}

function suffixCharge(text: string): { body: string; charge: number } | null {
  const match = ION_SUFFIX.exec(text);
  if (!match || match[1].trim() === "") {
    return null;
  }
  const signs = match[3];
  const magnitude = match[2] === "" ? signs.length : Number(match[2]);
  const negative = signs.startsWith("-") || signs.startsWith("−");
  return { body: match[1].trim(), charge: negative ? -magnitude : magnitude };
}

/**
 * An element by symbol, name or atomic number, with an optional charge written after it
 * (`Fe3+`, `Fe^3+`, `Cl-`, `O2-`) or typed in `chargeText`.
 */
export function readSpecies(text: string, chargeText: string): SpeciesInput {
  const raw = text.trim();
  if (raw === "") {
    return { ok: false, reason: "empty" };
  }
  let body = raw;
  let charge: number | null = null;
  const suffix = suffixCharge(raw);
  if (suffix && !findElement(raw)) {
    body = suffix.body;
    charge = suffix.charge;
  }
  const element = findElement(body);
  if (!element) {
    return { ok: false, reason: "unknown" };
  }
  if (charge === null) {
    const typed = chargeText.trim().replace("−", "-").replace(LEADING_PLUS, "");
    charge = typed === "" ? 0 : Number(typed);
    if (!Number.isInteger(charge)) {
      return { ok: false, reason: "badCharge" };
    }
  }
  return {
    ok: true,
    z: element.z,
    symbol: element.symbol,
    name: element.name,
    charge,
  };
}
